import { api } from "../../api";

const BASE_URL = "/v2/researcher-checklists";

// Types
export interface ResearcherProgram {
  program_id: number;
  program_title: string;
  description?: string;
  type?: string;
  architecture_version?: number;
  scheduling_type?: string;
  total_items: number;
  completed_items: number;
  run_id?: number | null;
  run_name?: string | null;
  company_name?: string;
  is_master?: boolean;
}

export interface ChecklistItemComment {
  comment_id: number;
  program_item_id: number;
  user_id: number;
  comment_text: string;
  comment_type: "COMMENT" | "SUBMISSION" | "APPROVAL" | "REJECTION" | "RESUBMISSION";
  is_internal: boolean;
  created_at: string;
  updated_at: string;
  author: {
    user_id: number;
    username: string;
    display_name: string;
    email: string;
    role: number;
  };
}

export interface ResearcherReport {
  report_id: number;
  report_title: string;
  severity_category?: string;
  state?: string;
  retest_status?: string | null;
  is_restricted_for_business?: boolean;
  submitted_date?: string;
}

export interface ChecklistItem {
  program_item_id: number;
  program_checklist_id: number;
  framework?: string;
  template_item_id: number;
  item_order: number;
  category?: string;
  title: string;
  description?: string;
  test_objective?: string;
  expected_evidence?: string;
  references?: string;
  requirement_id?: string;
  l1?: boolean;
  l2?: boolean;
  l3?: boolean;
  is_web?: boolean;
  is_api?: boolean;
  is_required: boolean;
  status: "NOT_STARTED" | "IN_PROGRESS" | "PENDING_REVIEW" | "REJECTED" | "APPROVED" | "COMPLETED" | "NOT_APPLICABLE" | "FAILED";
  actual_evidence?: string;
  evidence_files?: string[];
  notes?: string;
  assigned_to?: number[];
  tested_by?: number;
  tested_at?: string;
  reviewed_by?: number;
  reviewed_at?: string;
  bug_found?: boolean;
  linked_report_ids?: number[];
  version?: number;
  program_id?: number;
  program_title?: string;
  company_name?: string;
  isSnapshot?: boolean;
  run_id?: number | null;
  run_title?: string;
  programChecklist?: {
    program_id: number;
    program?: {
      program_id: number;
    };
  };
  tester?: {
    user_id: number;
    username: string;
    display_name: string;
    email: string;
  };
  reviewer?: {
    user_id: number;
    username: string;
    display_name: string;
    email: string;
  };
  linkedReports?: ResearcherReport[];
  is_assigned_to_me?: boolean;
  is_assigned_to_others?: boolean;
  is_assigned?: boolean;
  assigned_researchers?: Array<{
    user_id: number;
    username: string;
    display_name: string;
    email: string;
  }>;
  my_result?: {
    status: string;
    actual_evidence?: string;
    evidence_files?: string[];
    notes?: string;
    bug_found?: boolean;
    linked_report_ids?: number[];
    user_id: number;
    tested_at: string;
  };
  all_results?: Array<{
    result_id: number;
    status: string;
    actual_evidence?: string;
    evidence_files?: string[];
    notes?: string;
    bug_found?: boolean;
    linked_report_ids?: number[];
    user_id: number;
    tested_at: string;
    researcher?: {
      user_id: number;
      username: string;
      display_name: string;
      email: string;
    };
  }>;
  outcome_status?: string;
  tested_by_researchers?: Array<{
    user_id: number;
    username: string;
    display_name: string;
    email: string;
  }>;
}

export interface ProgramChecklist {
  program_checklist_id: number;
  framework?: string;
  program_id: number;
  template_id: number;
  checklist_name: string;
  description?: string;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "BLOCKED";
  completion_percentage: number;
  items: ChecklistItem[];
}

export const researcherChecklistApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get all programs accessible to researcher
    getResearcherPrograms: builder.query<{ success: boolean; data: ResearcherProgram[] }, void>({
      query: () => ({
        url: `${BASE_URL}/programs`,
        method: "GET"
      }),
      providesTags: ["ResearcherPrograms"]
    }),

    // Get checklist items for a program or specific run
    getProgramChecklistItems: builder.query<{ success: boolean; data: ProgramChecklist[] }, { programId: number; runId?: number | null }>({
      query: ({ programId, runId }) => ({
        url: `${BASE_URL}/programs/${programId}/checklist-items`,
        method: "GET",
        params: runId ? { runId } : undefined
      }),
      providesTags: (_result, _error, { programId, runId }) => [
        { type: "ChecklistItems", id: runId ? `run-${runId}` : `prog-${programId}` }
      ]
    }),

    // Get researcher's own reports for a program
    getResearcherReportsForProgram: builder.query<{ success: boolean; data: ResearcherReport[] }, { programId: number; runId?: number | null }>({
      query: ({ programId, runId }) => ({
        url: `${BASE_URL}/programs/${programId}/reports`,
        method: "GET",
        params: runId ? { runId } : undefined
      }),
      providesTags: (_result, _error, { programId }) => [
        { type: "ResearcherReports", id: programId }
      ]
    }),

    // Get a single checklist item by ID
    getChecklistItemById: builder.query<{ success: boolean; data: ChecklistItem }, number>({
      query: (itemId) => ({
        url: `${BASE_URL}/items/${itemId}`,
        method: "GET"
      }),
      providesTags: (_result, _error, itemId) => [
        { type: "ChecklistItems", id: itemId }
      ]
    }),

    // Get all items for review (QA/ADMIN)
    getAllItemsForReview: builder.query<{ success: boolean; data: ChecklistItem[] }, void>({
      query: () => ({
        url: `${BASE_URL}/review-queue`,
        method: "GET"
      }),
      providesTags: ["ChecklistItems"]
    }),

    // Update a checklist item (Researcher)
    updateResearcherChecklistItem: builder.mutation<{ success: boolean; data: ChecklistItem }, FormData>({
      query: (formData) => ({
        url: `${BASE_URL}/items/update`,
        method: "POST",
        data: formData
      }),
      invalidatesTags: (_result, _error, formData) => {
        const itemId = formData.get("itemId");
        return [
          "ChecklistItems",
          { type: "ChecklistItems", id: itemId ? Number(itemId) : undefined },
          "ItemComments"
        ];
      }
    }),

    // Submit item for review
    submitItemForReview: builder.mutation<
      { success: boolean; data: ChecklistItem },
      { itemId: number; comment?: string }
    >({
      query: (body) => ({
        url: `${BASE_URL}/items/submit`,
        method: "POST",
        data: body
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        "ChecklistItems",
        { type: "ItemComments", id: itemId }
      ]
    }),

    // Get comments for an item
    getItemComments: builder.query<{ success: boolean; data: ChecklistItemComment[] }, number>({
      query: (itemId) => ({
        url: `${BASE_URL}/items/${itemId}/comments`,
        method: "GET"
      }),
      providesTags: (_result, _error, itemId) => [
        { type: "ItemComments", id: itemId }
      ]
    }),

    // Add a comment
    addItemComment: builder.mutation<
      { success: boolean; data: ChecklistItemComment },
      { itemId: number; commentText: string; isInternal?: boolean }
    >({
      query: (body) => ({
        url: `${BASE_URL}/items/comments`,
        method: "POST",
        data: body
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        { type: "ItemComments", id: itemId }
      ]
    }),

    // Approve item (QA/ADMIN)
    approveChecklistItem: builder.mutation<
      { success: boolean; data: ChecklistItem },
      { itemId: number; comment?: string }
    >({
      query: (body) => ({
        url: `${BASE_URL}/items/approve`,
        method: "POST",
        data: body
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        "ChecklistItems",
        { type: "ItemComments", id: itemId }
      ]
    }),

    // Reject item (QA/ADMIN)
    rejectChecklistItem: builder.mutation<
      { success: boolean; data: ChecklistItem },
      { itemId: number; comment: string }
    >({
      query: (body) => ({
        url: `${BASE_URL}/items/reject`,
        method: "POST",
        data: body
      }),
      invalidatesTags: (_result, _error, { itemId }) => [
        "ChecklistItems",
        { type: "ItemComments", id: itemId }
      ]
    })
  })
});

export const {
  useGetResearcherProgramsQuery,
  useGetProgramChecklistItemsQuery,
  useGetResearcherReportsForProgramQuery,
  useGetChecklistItemByIdQuery,
  useGetAllItemsForReviewQuery,
  useUpdateResearcherChecklistItemMutation,
  useSubmitItemForReviewMutation,
  useGetItemCommentsQuery,
  useAddItemCommentMutation,
  useApproveChecklistItemMutation,
  useRejectChecklistItemMutation
} = researcherChecklistApi;

